import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { ConnectivityService } from './connectivity.service';
import { NetworkStatus } from './connectivity.model';

/** How often the status is re-checked while offline, in case an event is missed. */
export const CONNECTIVITY_POLLING_INTERVAL_MS = 30_000;

/**
 * @description
 * Browser implementation of {@link ConnectivityService}.
 *
 * Uses the browser capabilities: `navigator.onLine` plus the `online`/`offline`
 * events, re-checking on tab visibility changes. A polling fallback runs only
 * while offline, so a missed event does not leave the app stuck without
 * connection.
 *
 * Meant to be used directly on web apps (backoffice) and as the base class of
 * the Capacitor implementation, which reuses the polling/lifecycle handling and
 * replaces the status source.
 */
@Injectable()
export class WebConnectivityService extends ConnectivityService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private pollIntervalId?: ReturnType<typeof setInterval>;
  private watching = false;

  public async start(): Promise<void> {
    if (this.started) {
      return;
    }

    this.started = true;

    try {
      this.applyStatus(this.readStatus());
      this.watchBrowserEvents();
    } catch (error) {
      // Never reject: the app bootstrap waits for this, and the polling/listeners can still correct
      // the status later.
      console.error('[ConnectivityService] Error starting the connectivity status:', error);
    }
  }

  /** Returns the current connectivity status. Overridden per platform. */
  protected readStatus(): NetworkStatus {
    return { connected: navigator.onLine };
  }

  /** Applies a status and keeps the offline polling in sync. */
  protected applyStatus(status: NetworkStatus): void {
    // Platform listeners are not patched by zone.js, so the state is updated
    // inside the Angular zone to make sure change detection runs.
    this.zone.run(() => this.setStatus(status));
    this.syncOfflinePolling();
  }

  /** Re-reads the status. Used by the polling fallback and by resume events. */
  protected refresh(): void | Promise<void> {
    this.applyStatus(this.readStatus());
  }

  /** Removes every listener and stops the polling fallback. */
  protected stop(): void {
    this.watching = false;
    window.removeEventListener('online', this.handleBrowserStatusChange);
    window.removeEventListener('offline', this.handleBrowserStatusChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearOfflinePolling();
  }

  private watchBrowserEvents(): void {
    if (this.watching) {
      return;
    }

    this.watching = true;
    window.addEventListener('online', this.handleBrowserStatusChange);
    window.addEventListener('offline', this.handleBrowserStatusChange);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.registerStopOnDestroy();
  }

  /** Makes sure the platform watchers are removed when the service is destroyed. */
  protected registerStopOnDestroy(): void {
    this.destroyRef.onDestroy(() => this.stop());
  }

  private readonly handleBrowserStatusChange = (): void => {
    this.refresh();
  };

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.refresh();
    }
  };

  private syncOfflinePolling(): void {
    if (this.isOnline()) {
      this.clearOfflinePolling();
      return;
    }

    if (this.pollIntervalId) {
      return;
    }

    // Created outside Angular: the timer itself must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.pollIntervalId = setInterval(() => {
        void this.refresh();
      }, CONNECTIVITY_POLLING_INTERVAL_MS);
    });
  }

  private clearOfflinePolling(): void {
    if (!this.pollIntervalId) {
      return;
    }

    clearInterval(this.pollIntervalId);
    this.pollIntervalId = undefined;
  }
}
